import React, { useEffect, useRef, ReactElement } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { getBackgroundTheme, getPatternModal, getTextColor } from '../../style';
import Button from '../buttons/Button';
import CloseIcon from '@material-ui/icons/Close';
import {
  sendToastMessageView,
  sendToastMessageClick,
  sendToastFavouriteView,
  sendToastFavouriteClick,
  sendToastOfferView,
  sendToastOfferClick,
} from '../../actions/toastTrackAction';
import { triggerTracking } from '../../utils/helpers';
import ModalPattern from '../../components/common/ModalPattern';
import { StarIcon } from '../icons/favoriteIcons';
import {
  ActionProps,
  ActivityAlertProps,
  ActivityColorProps,
  AlertContainerProps,
  ColorProps,
  OfferAlertProps,
} from '../types/activityAlert';
import breakpoints from 'theme/material/breakpoints';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';

const activityColor: { [key: string]: ActivityColorProps } = {
  blue: {
    panelColor: '#2B8FD7',
    fontColor: '#81ECEC',
  },
  green: {
    panelColor: '#00B894',
    fontColor: '#005B49',
  },
  gold: {
    panelColor: '#BF9200',
    fontColor: '#7F5700',
  },
  white: {
    panelColor: '#FFFFFF',
    fontColor: '#212121',
  },
  red: {
    panelColor: getPatternModal().titleColor,
    fontColor: getPatternModal().titleColor,
  },
};

const AlertContainer = styled('div')(() => ({
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  width: '460px',
  minHeight: '72px',
  padding: '0px',
  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.4)',
  cursor: 'pointer',
  marginTop: '10px',
  background: getPatternModal().bgGradient,
  border: getPatternModal().borderStyle,

  [breakpoints.down(479.05)]: {
    width: '296px',
    minHeight: '68px',
    padding: '4px 14px',
    margin: '10px auto 0',
    position: 'fixed',
    bottom: '5%',
    left: '0',
    right: '0',
  },
}));

const Avatar = styled('div')<AlertContainerProps>(
  {
    width: 100,
    height: 100,
    background: '#000000',
    flexShrink: 0,
    borderRadius: 4,
    [breakpoints.down(479.05)]: {
      width: 75,
      height: 75,
    },
  },
  ({ backgroundUrl }) =>
    backgroundUrl && {
      backgroundImage: `url(${backgroundUrl})`,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    }
);

const MessageContainer = styled('div')({
  flex: 1,
  paddingLeft: '14px',
  [breakpoints.down(479.05)]: {
    paddingLeft: '10px',
  },
});

const Message = styled('div')<ColorProps>(({ color }) => ({
  fontSize: '22px',
  fontWeight: 800,
  color: `${color ? activityColor[color].fontColor : 'inherit'}`,
  [breakpoints.down(479.05)]: {
    fontSize: '14px',
  },
}));

const Action = styled('div')<ActionProps>(
  {
    color: 'white',
    fontSize: 16,
    fontWeight: 700,
    textAlign: 'right',
    paddingTop: 10,
    padding: '8px 20px',
    borderRadius: 6,
    position: 'absolute',
    right: 15,
    bottom: 15,
    [breakpoints.down('md')]: {
      fontSize: 12,
      right: 5,
      bottom: 5,
    },
  },
  ({ bgColor }) =>
    bgColor && {
      background: activityColor[bgColor].panelColor,
    }
);

const OfferAlertContainer = styled('div')({
  borderRadius: '20px',
  display: 'flex',
  flexWrap: 'wrap',
  width: '360px',
  minHeight: '400px',
  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.4)',
  cursor: 'pointer',
  margin: '40px auto 0',
  position: 'relative',
  backgroundClip: 'padding-box',
  boxSizing: 'border-box',
  border: 'solid 5px transparent',
  top: '50px',
  '&:before': {
    content: "''",
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: -1,
    margin: '-5px',
    borderRadius: 'inherit',
    background: 'linear-gradient(135deg, #ff0099 0%, #ffba72 100%)',
    boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.3)',
  },
  [breakpoints.down(479.05)]: {
    top: '47%',
    transform: 'translateY(-50%)',
  },
  [breakpoints.up(321)]: {
    width: '300px',
    margin: '15px auto 0',
  },
  [breakpoints.up(480)]: {
    width: '320px',
    margin: '30px auto 0',
  },
  [breakpoints.up(640)]: {
    width: '360px',
    margin: '40px auto 0',
  },
});

const OfferAvatar = styled('div')<AlertContainerProps>(
  {
    width: '100%',
    height: 340,
    background: '#000000',
    flexShrink: 0,
    borderRadius: '20px 20px 0px 0px',
    position: 'relative',
  },
  ({ backgroundUrl }) =>
    backgroundUrl && {
      backgroundImage: `url(${backgroundUrl})`,
      backgroundPosition: 'top',
      backgroundSize: 'cover',
    }
);

const OfferMessageContainer = styled('div')(props => ({
  flex: 1,
  padding: '14px',
  borderRadius: '0px 0px 20px 20px',
  backgroundColor: props.color ? activityColor[props.color].panelColor : getBackgroundTheme().block,
}));

const OfferMessage = styled('div')(props => ({
  fontSize: '20px',
  fontWeight: 600,
  textAlign: 'center',
  color: props.color ? activityColor[props.color].fontColor : getTextColor().DARK.color,
}));

const OfferInfo = styled('div')`
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  padding: 10px 0;
`;

const OfferSpan = styled('span')(() => ({
  borderRadius: 5,
  padding: 10,
  background: getBackgroundTheme().block,
  ...getTextColor().DARK,
}));

const BackdropContainer = styled('div')`
  background: rgba(0, 0, 0, 0.4);
  top: 0;
  left: 0;
  position: fixed;
  width: 100%;
  height: 100%;
  text-align: center;
`;

const OfferClose = styled('a')`
  font-weight: 600;
  position: absolute;
  right: 10px;
  top: 10px;
  filter: drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.8));
  color: #fff;
  z-index: 4;
`;

const OfferProfileContainer = styled('div')`
  text-shadow: 0px 0px 4px rgba(0, 0, 0, 0.8);
  color: #fff;
  position: absolute;
  bottom: 10px;
  left: 10px;
  text-align: left;
  z-index: 3;
`;

const OfferAmount = styled('span')(() => ({
  fontSize: 22,
  fontWeight: 600,
  ...getTextColor().DARK,
}));

const OfferUsername = styled('div')`
  font-weight: bold;
`;

const OfferProfileDetail = styled('div')``;

const StyleLink = styled('a')(() => ({
  ...getTextColor().DARK,
}));

const Gradient = styled('div')({
  background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 81%, rgba(0,0,0,1) 100%)',
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  zIndex: 2,
});

const timeOutFunc = (timeout: number, onTimeout: () => void) => {
  if (timeout > 0 && typeof onTimeout === 'function') {
    setTimeout(() => onTimeout(), timeout);
  }
};

const modalPatternCustomStyle = {
  width: '100%',
  position: 'relative',
};

export const OfferAlert = ({
  timeout,
  onTimeout,
  color,
  avatar,
  message,
  offerAmount,
  userName,
  userAge,
  userLocation,
  onClick,
  onClose,
}: OfferAlertProps): ReactElement => {
  useEffect(() => {
    timeOutFunc(timeout, onTimeout);
  }, [timeout, onTimeout]);

  return (
    <BackdropContainer>
      <OfferAlertContainer id="offer-alert" color={color}>
        <OfferAvatar backgroundUrl={avatar}>
          <OfferClose
            onClick={e => {
              e.preventDefault();
              onClose && onClose();
            }}
          >
            <CloseIcon style={{ fontSize: 28 }} />
          </OfferClose>
          <OfferProfileContainer>
            <OfferUsername>{userName}</OfferUsername>
            <OfferProfileDetail>
              {' '}
              {userAge} • {userLocation}
            </OfferProfileDetail>
          </OfferProfileContainer>
          <Gradient />
        </OfferAvatar>
        <OfferMessageContainer>
          <OfferMessage color={color}>
            <OfferSpan>{message}</OfferSpan>
          </OfferMessage>
          <OfferInfo>
            <OfferAmount>{offerAmount}</OfferAmount>
          </OfferInfo>
          <OfferInfo>
            <Button buttonType="primaryRed" onClick={onClick} isDefault>
              Send a message
            </Button>
          </OfferInfo>
          <OfferInfo>
            <StyleLink
              onClick={e => {
                e.preventDefault();
                onTimeout();
              }}
            >
              Not yet
            </StyleLink>
          </OfferInfo>
        </OfferMessageContainer>
      </OfferAlertContainer>
    </BackdropContainer>
  );
};

const setToastAction = (e: Event, eventType: string) => {
  switch (eventType) {
    case 'click_toast_message':
      sendToastMessageClick();
      break;
    case 'click_toast_favourite':
      sendToastFavouriteClick();
      break;
    case 'click_toast_offer':
      sendToastOfferClick();
      break;
    default:
      return;
  }
};

const ActivityAlert = ({
  timeout,
  onTimeout,
  color = 'red',
  avatar,
  message,
  actionText,
  notificationType,
  onClick,
  hasFavIcon = false,
}: ActivityAlertProps): ReactElement => {
  const { isMobile } = useMediaBreakpoints();

  const clickToastForMessage = useRef<HTMLElement | null>(null);
  const clickToastForFavourite = useRef<HTMLElement | null>(null);
  const clickToastForOffer = useRef<HTMLElement | null>(null);

  useEffect(() => {
    timeOutFunc(timeout, onTimeout);
    clickToastForMessage.current = document.getElementById('activity-alert-mail');
    clickToastForFavourite.current = document.getElementById('activity-alert-favorites');
    clickToastForOffer.current = document.getElementById('activity-alert-offers');
    triggerTracking('click_toast_message', clickToastForMessage, setToastAction);
    triggerTracking('click_toast_favourite', clickToastForFavourite, setToastAction);
    triggerTracking('click_toast_offer', clickToastForOffer, setToastAction);
  }, [onTimeout, timeout]);

  useEffect(() => {
    switch (notificationType) {
      case 'favorites':
        sendToastFavouriteView();
        break;
      case 'mail':
        sendToastMessageView();
        break;
      case 'offers':
        sendToastOfferView();
        break;
      default:
        return;
    }
  }, [notificationType]);

  return (
    <AlertContainer id={`activity-alert-${notificationType}`} color={color} onClick={onClick}>
      <ModalPattern
        padding={isMobile ? 5 : 15}
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        customStyle={modalPatternCustomStyle}
        backgroundSize="65%"
      >
        <Avatar backgroundUrl={avatar} />
        <MessageContainer>
          <Message color={'red'}>
            {hasFavIcon && (
              <StarIcon
                width={isMobile ? 12 : 18}
                height={isMobile ? 12 : 18}
                isActive
                fill={getPatternModal().titleColor}
              />
            )}
            {message}
          </Message>
          <Action bgColor={color}>
            {hasFavIcon && <StarIcon width={isMobile ? 12 : 18} height={isMobile ? 12 : 18} isActive fill="#FFFFFF" />}
            {actionText}
          </Action>
        </MessageContainer>
      </ModalPattern>
    </AlertContainer>
  );
};

ActivityAlert.propTypes = {
  /** Defines color theme of activity alert */
  color: PropTypes.oneOf(['blue', 'green', 'gold', 'white', 'red']),
  /** Avatar url */
  avatar: PropTypes.string,
  /** Message */
  message: PropTypes.string.isRequired,
  /** Text of action button */
  actionText: PropTypes.string.isRequired,
  /** Function to handle clicking on activity alert */
  onClick: PropTypes.func.isRequired,
  /** Timer before ontimeout prop is triggered */
  timeout: PropTypes.number,
  /** Triggers upon finishing the timeout */
  onTimeout: PropTypes.func,
  /** Triggers close event */
  onClose: PropTypes.func,
  /** Alert Type */
  notificationType: PropTypes.string,
};

export default ActivityAlert;
